import React, { useEffect, useState } from "react";
import moment from "moment";

import Container from "../forms/controls/container";
import { StyledDatePicker } from "./styled/date-picker";
import DatePickerPostfix from "./DatePickerPostfix";
import DatePickerCloseBtn from "./DatePickerCloseBtn";
import { parseDateFormat } from "../forms/helper";
import { iControlProps } from "../forms/types";
import filterDates from "./functions/filterDates";
import findMinDate from "./functions/findMinDate";
import findMaxDate from "./functions/findMaxDate";
import formatCalendarDay from "./functions/formatCalendarDay";
import autoSeparator from "./functions/autoSeparator";

type DateRangeProps = iControlProps & {
    onChange: (date: string) => void;
    error: string;
    dateFormat?: string;
    placeholder?: string;
    inputs: { id: string; label: string; name: string }[];
    only_date_in_past?: boolean;
    only_date_in_future?: boolean;
    include_today_in_dates?: boolean;
    exclude_weekends?: boolean;
    excluded_dates?: string;
    field_boi_max_end_date_rule?: string;
    field_boi_min_start_date_rule?: string;
    field_boi_min_date_range_rule?: string;
    field_boi_max_date_range_rule?: string;
};

const DateRange = (props: DateRangeProps) => {
    const {
        inputs,
        placeholder,
        onChange,
        dateFormat: format,
        error: propsError,
        value: propsValue = { [inputs[0].id]: "", [inputs[1].id]: "" }
    } = props;
    const ymdDashDateFormat = "YYYY-MM-DD";
    const dateFormat = parseDateFormat(format || "dmy");
    const dateFormatUpper = dateFormat.toUpperCase();
    const momentStart = moment(
        propsValue ? propsValue[inputs[0].id] : null,
        ymdDashDateFormat
    );
    const momentEnd = moment(
        propsValue ? propsValue[inputs[1].id] : null,
        ymdDashDateFormat
    );

    const [error, setError] = useState<string>(propsError);
    const [isOpen, setIsOpen] = useState<boolean>(false);
    const [value, setValue] = useState<string>("");
    const [startDate, setStartDate] = useState<Date | null>(null);
    const [endDate, setEndDate] = useState<Date | null>(null);

    useEffect(() => {
        if (momentStart.isValid() && momentEnd.isValid()) {
            setValue(
                `${momentStart.format(dateFormatUpper)} - ${momentEnd.format(
                    dateFormatUpper
                )}`
            );
            setStartDate(momentStart.toDate());
            setEndDate(momentEnd.toDate());
        }
    }, []);

    const [minDate, setMinDate] = useState(findMinDate(props));
    const [maxDate, setMaxDate] = useState(findMaxDate(props));
    const filterDate = (date: Date) => filterDates(date, props);

    const handleChange = (
        dates: (Date | string)[],
        event: React.ChangeEvent<HTMLInputElement> | null = null
    ) => {
        let valueStart = "";
        let valueEnd = "";
        let rangeValue = null;
        let strValue = "";
        let startDate = null;
        let endDate = null;

        if (dates && dates.filter(date => !!date).length) {
            if (
                dates[0] &&
                ((event?.type === "change" && event?.target.value) ||
                    event?.type === "click" ||
                    event?.type === "blur")
            ) {
                const valueFormat =
                    typeof dates[0] === "string"
                        ? dateFormat.toUpperCase()
                        : ymdDashDateFormat;
                const momentStartDate = moment(dates[0], valueFormat);

                valueStart = momentStartDate.format(ymdDashDateFormat);
                if (momentStartDate.isValid()) {
                    startDate = momentStartDate.toDate();
                    strValue = `${momentStartDate.format(dateFormatUpper)} - `;
                }
            }

            if (
                dates[1] &&
                ((event?.type === "change" && event?.target.value) ||
                    event?.type === "click" ||
                    event?.type === "blur")
            ) {
                const momentEndDate = moment(
                    dates[1],
                    typeof dates[1] === "string"
                        ? dateFormat.toUpperCase()
                        : ymdDashDateFormat
                );
                valueEnd = momentEndDate.format(ymdDashDateFormat);
                if (momentEndDate.isValid()) {
                    endDate = momentEndDate.toDate();
                    strValue += momentEndDate.format(dateFormatUpper);
                }
                setIsOpen(false);
            }
            rangeValue = {
                [inputs[0].id]: valueStart,
                [inputs[1].id]: valueEnd
            };
        }
        onChange(rangeValue);
        setValue(strValue);
        setStartDate(startDate);
        setEndDate(endDate);
        setError(propsError);
    };

    const handleRawChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.type === "change") {
            let datesStr = event.target.value;

            if (
                (event.nativeEvent as InputEvent).inputType ===
                "deleteContentBackward"
            ) {
                setValue(datesStr);
                return;
            }

            if (datesStr.length > 23) return;
            if (/([^\d\s-./])/g.test(datesStr)) datesStr = value;

            let dates = datesStr.split(" - ");

            if (datesStr.length >= 10 && !datesStr.includes(" - "))
                dates = [
                    datesStr.replaceAll(" ", "").substring(0, 10),
                    datesStr.replaceAll(" ", "").substring(11)
                ];

            dates = dates.map(date =>
                autoSeparator(date.replaceAll(" ", ""), event, format || "dmy")
            );
            setValue(dates.join(" - "));

            const datesLen = dates.join(" - ").length;
            (datesLen === 23 || datesLen === 13) && handleChange(dates, event);
        }
    };

    useEffect(() => {
        setMinDate(findMinDate(props));
        setMaxDate(findMaxDate(props));
    }, [value]);

    useEffect(() => {
        setError(propsError);
    }, [propsError]);

    return (
        <Container {...props} error={error}>
            <DatePickerPostfix
                isActive={isOpen}
                onClick={() => setIsOpen(!isOpen)}
                cssClass="u-width-70 u-width-sm-100"
            >
                <StyledDatePicker
                    {...props}
                    selectsRange
                    open={isOpen}
                    value={value}
                    startDate={startDate}
                    endDate={endDate}
                    openToDate={startDate}
                    dateFormat={dateFormat}
                    filterDate={filterDate}
                    onChange={handleChange}
                    onChangeRaw={handleRawChange}
                    onFocus={() => {
                        setIsOpen(true);
                        setError("");
                    }}
                    onBlur={(event: React.ChangeEvent<HTMLInputElement>) => {
                        const dates = event.target.value.split(" - ");
                        handleChange(dates, event);
                        setError(propsError);
                        setIsOpen(false);
                    }}
                    onClickOutside={() => setError(propsError)}
                    formatWeekDay={formatCalendarDay}
                    className="boi-datepicker"
                    calendarClassName={`boi-calendar boi-date-range-calendar ${
                        error ? "boi-calendar-error" : ""
                    }`}
                    placeholderText={
                        placeholder || `${dateFormatUpper} - ${dateFormatUpper}`
                    }
                    yearDropdownItemNumber={100}
                    dateFormatCalendar=" "
                    dropdownMode="scroll"
                    isClearable={!!value}
                    minDate={minDate}
                    maxDate={maxDate}
                    showYearDropdown
                    showMonthDropdown
                    useShortMonthInDropdown
                    scrollableYearDropdown
                    showPopperArrow={false}
                >
                    <DatePickerCloseBtn
                        onClick={(
                            event: React.ChangeEvent<HTMLInputElement>
                        ) => {
                            setIsOpen(false);
                            handleChange(value.split(" - "), event);
                        }}
                        error={error}
                        text="CANCEL"
                    />
                </StyledDatePicker>
            </DatePickerPostfix>
        </Container>
    );
};

export default DateRange;
